import { useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { usePriceModifiersApi } from "./usePriceModifiersApi";

const queryKeys = {
    priceModifiers: (propertyId, normalizedFilters) => [
        "priceModifiers",
        { propertyId, ...normalizedFilters },
    ],
};

/**
 * Price modifiers for a property (optional filters match GET query params on the API).
 * @param {string|null|undefined} propertyId
 * @param {{ roomId?: string|number, planId?: string|number, adjustmentScope?: string }} [filters]
 */
export const usePriceModifiersData = (propertyId, filters) => {
    const { findAllPriceModifiers, createPriceModifier, updatePriceModifier, deletePriceModifier } =
        usePriceModifiersApi();

    const queryClient = useQueryClient();
    const roomId = filters?.roomId ?? null;
    const planId = filters?.planId ?? null;
    const adjustmentScope = filters?.adjustmentScope ?? null;
    const normalizedFilters = useMemo(
        () => ({ roomId, planId, adjustmentScope }),
        [roomId, planId, adjustmentScope]
    );

    const queryKey = queryKeys.priceModifiers(propertyId, normalizedFilters);

    const apiFilterOptions = useMemo(() => {
        const o = {};
        if (normalizedFilters.roomId != null) {
            o.roomId = normalizedFilters.roomId;
        }
        if (normalizedFilters.planId != null) {
            o.planId = normalizedFilters.planId;
        }
        if (normalizedFilters.adjustmentScope != null) {
            o.adjustmentScope = normalizedFilters.adjustmentScope;
        }
        return Object.keys(o).length ? o : undefined;
    }, [normalizedFilters]);

    const queryResults = useQuery({
        queryKey,
        queryFn: () => findAllPriceModifiers(propertyId, apiFilterOptions),
        staleTime: defaultStaleTime,
        enabled: !!propertyId,
    });

    const _createModifier = async (modifier) => {
        const createdModifier = await createPriceModifier(propertyId, modifier);
        queryClient.setQueryData(queryKey, (old) => [...(old || []), createdModifier]);
    };

    const _updateModifier = async (modifier) => {
        const updatedModifier = await updatePriceModifier(propertyId, modifier);
        queryClient.setQueryData(queryKey, (old) =>
            (old || []).map((m) => (String(m.id) === String(updatedModifier.id) ? updatedModifier : m))
        );
    };

    const _deleteModifier = async (id) => {
        await deletePriceModifier(propertyId, id);
        queryClient.setQueryData(queryKey, (old) =>
            (old || []).filter((m) => String(m.id) !== String(id))
        );
    };

    return {
        queryResults,
        createModifier: _createModifier,
        updateModifier: _updateModifier,
        deleteModifier: _deleteModifier,
    };
};
